import React, { useState, useEffect } from 'react';
import * as api from '../../api/client';

interface LoginPageProps {
  onLogin: (data: { token: string; user_id: string; username: string; role: string }) => void;
}

export default function LoginPage({ onLogin }: LoginPageProps) {
  // Default google_login=false so the button doesn't show until
  // /api/health confirms OAuth is actually configured — prevents the
  // 'click → 404' loop on a fresh deployment that hasn't filled in
  // the Google client_id yet. register defaults to true (always-on
  // self-serve registration is the legacy behavior).
  const [features, setFeatures] = useState<{ google_login: boolean; register: boolean }>({ google_login: false, register: true });
  useEffect(() => {
    fetch('/api/health').then(r => r.json()).then(d => {
      if (d.features) setFeatures(d.features);
    }).catch(() => {});
  }, []);

  const [showPasswordLogin, setShowPasswordLogin] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState('');
  const [focusedField, setFocusedField] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isRegister) {
        const data = await api.register(username, email, password);
        onLogin(data);
        return;
      }
      const data = await api.login(username, password);
      onLogin(data);
    } catch (err: any) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = (field: string): React.CSSProperties => ({
    width: '100%',
    padding: '10px 14px',
    fontSize: 'var(--fs-base)',
    border: '1px solid var(--border)',
    borderColor: focusedField === field ? 'var(--border-strong)' : 'var(--border)',
    borderRadius: 4,
    background: 'var(--bg-surface)',
    color: 'var(--text-primary)',
    outline: 'none',
    fontFamily: 'inherit',
    transition: 'border-color 0.3s',
  });

  return (
    <div style={{ height: 'var(--app-height)', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-primary)', paddingTop: '8vh' }}>
      <div style={{ width: 320, background: 'var(--bg-primary)', borderRadius: 0, padding: 32, boxShadow: 'none', border: 'none' }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <h1 style={{ fontSize: 14, fontWeight: 400, color: 'var(--text-primary)', marginBottom: 8, letterSpacing: '0.01em' }}>
            PitchPilot
          </h1>
          <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {showPasswordLogin
              ? (isRegister ? 'Create your account' : 'Sign in with password')
              : 'Sign in to continue'}
          </p>
        </div>

        {/* Google OAuth */}
        {features.google_login && <a
          href="/api/auth/google/start"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            width: '100%', padding: '10px 16px', fontSize: 'var(--fs-sm)', fontWeight: 400,
            background: 'var(--bg-surface)', color: 'var(--text-primary)',
            border: '1px solid var(--border)', borderRadius: 4,
            textDecoration: 'none', fontFamily: 'inherit',
            transition: 'border-color 0.3s',
          }}
          onMouseOver={e => { e.currentTarget.style.borderColor = 'var(--border-strong)'; }}
          onMouseOut={e => { e.currentTarget.style.borderColor = 'var(--border)'; }}
        >
          <svg width="16" height="16" viewBox="0 0 48 48">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          </svg>
          Sign in with Google
        </a>}

        {/* Password login — expandable secondary option */}
        {!showPasswordLogin ? (
          <div style={{ textAlign: 'center', marginTop: 20 }}>
            <button
              onClick={() => setShowPasswordLogin(true)}
              style={{
                fontSize: 'var(--fs-sm)', color: 'var(--text-muted)',
                background: 'none', border: 'none', cursor: 'pointer',
                fontFamily: 'inherit', textDecoration: 'underline',
                textUnderlineOffset: 2,
              }}
            >
              Sign in with username &amp; password
            </button>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', margin: '20px 0 16px' }}>
              <div style={{ flex: 1, borderTop: '1px solid var(--border)' }} />
              <span style={{ padding: '0 12px', fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>or</span>
              <div style={{ flex: 1, borderTop: '1px solid var(--border)' }} />
            </div>

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 12 }}>
                <input
                  type={isRegister ? 'text' : 'email'}
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  placeholder={isRegister ? 'Name' : 'Email'}
                  required
                  onFocus={() => setFocusedField('username')}
                  onBlur={() => setFocusedField(null)}
                  style={inputStyle('username')}
                />
              </div>
              {isRegister && (
                <div style={{ marginBottom: 12 }}>
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="Email"
                    required
                    onFocus={() => setFocusedField('email')}
                    onBlur={() => setFocusedField(null)}
                    style={inputStyle('email')}
                  />
                </div>
              )}
              <div style={{ marginBottom: 12 }}>
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Password"
                  required
                  onFocus={() => setFocusedField('password')}
                  onBlur={() => setFocusedField(null)}
                  style={inputStyle('password')}
                />
              </div>

              {error && (
                <div style={{ fontSize: 'var(--fs-sm)', color: '#ef4444', marginBottom: 10, textAlign: 'center' }}>{error}</div>
              )}

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%', padding: '9px 16px', fontSize: 'var(--fs-sm)', fontWeight: 400,
                  background: 'var(--text-primary)', color: 'var(--bg-primary)',
                  border: 'none', borderRadius: 4,
                  cursor: 'pointer', fontFamily: 'inherit',
                  opacity: loading ? 0.6 : 1, transition: 'opacity 0.3s',
                }}
              >
                {loading ? 'Please wait...' : isRegister ? 'Create Account' : 'Sign In'}
              </button>
            </form>

            {features.register && (
              <div style={{ textAlign: 'center', marginTop: 12 }}>
                <button
                  onClick={() => { setIsRegister(!isRegister); setError(''); }}
                  style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
                >
                  {isRegister ? 'Already have an account? Sign in' : 'Need an account? Register'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
